import { API_URL } from '../config/api';
import { isPreviewPlayer } from '../chat/isPreviewPlayer';

// Max concurrent presign requests when warming a batch.
const WARM_CONCURRENCY = 6;

class AssetResolver {
  constructor() {
    // Map<cacheKey, { url: string }> where url is a browser-owned blob URL.
    // cacheKey = "stock:{assetId}:v{assetVersion}"
    this._cache = new Map();

    // Map<cacheKey, Promise<string>>
    // Deduplicates concurrent resolve() calls for the same asset so only one
    // network request is made regardless of how many callers are waiting.
    this._inFlight = new Map();
  }

  // ---------------------------------------------------------------------------
  // Primary API
  // ---------------------------------------------------------------------------

  /**
   * Resolve a track/clip reference to a playable URL.
   *
   * Stock ref:  { sourceType: 'stock', assetId, assetVersion? }
   * User/legacy: { fileUrl }
   *
   * Returns null if the reference cannot be resolved.
   */
  async resolve(ref, token) {
    if (!ref) return null;
    if (isPreviewPlayer() && ref.sourceType === 'stock') {
      return window.__COOKUP_CHAT_ASSETS__?.[ref.assetId] || null;
    }
    if (ref.sourceType === 'stock') {
      if (!ref.assetId) return null;
      try {
        return await this._resolveStock(ref.assetId, ref.assetVersion || 1, token);
      } catch (err) {
        console.error(`[AssetResolver] Failed to resolve stock:${ref.assetId}:v${ref.assetVersion}`, err);
        return null;
      }
    }
    return ref.fileUrl || null;
  }

  /**
   * Warm the cache for a batch of stock refs before they are needed.
   * refs: Array<{ assetId, assetVersion? }>
   * Skips entries already present in the versioned cache. Runs at most WARM_CONCURRENCY
   * in parallel.
   */
  async warmBatch(refs, token) {
    if (isPreviewPlayer()) return;
    const seen = new Set();
    const toFetch = refs.filter(ref => {
      const key = this._cacheKey(ref.assetId, ref.assetVersion || 1);
      if (seen.has(key)) return false;
      seen.add(key);
      return this._isStale(key);
    });

    if (!toFetch.length) return;

    for (let i = 0; i < toFetch.length; i += WARM_CONCURRENCY) {
      await Promise.all(
        toFetch.slice(i, i + WARM_CONCURRENCY).map(ref =>
          this._resolveStock(ref.assetId, ref.assetVersion || 1, token).catch(() => null)
        )
      );
    }
  }

  invalidate(assetId, assetVersion = 1) {
    const key = this._cacheKey(assetId, assetVersion);
    this._revoke(this._cache.get(key));
    this._cache.delete(key);
  }

  invalidateAll() {
    this._cache.forEach((entry) => this._revoke(entry));
    this._cache.clear();
    // In-flight promises are left — they will populate the cache, then the
    // next fresh resolve will bypass the stale entry.
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  _cacheKey(assetId, assetVersion) {
    return `stock:${assetId}:v${assetVersion}`;
  }

  _isStale(cacheKey) {
    return !this._cache.has(cacheKey);
  }

  _revoke(entry) {
    if (entry?.url?.startsWith('blob:')
        && typeof URL !== 'undefined'
        && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(entry.url);
    }
  }

  async _resolveStock(assetId, assetVersion, token) {
    const key = this._cacheKey(assetId, assetVersion);

    // Cache hit
    if (!this._isStale(key)) {
      return this._cache.get(key).url;
    }

    // In-flight dedup: return the existing promise so N concurrent callers
    // all get the same result from one network request.
    if (this._inFlight.has(key)) {
      return this._inFlight.get(key);
    }

    const promise = this._fetchAndCache(assetId, key, token).finally(() => {
      this._inFlight.delete(key);
    });

    this._inFlight.set(key, promise);
    return promise;
  }

  async _fetchAndCache(assetId, cacheKey, token) {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(
      `${API_URL}/api/library/stock/${encodeURIComponent(assetId)}/content`,
      { headers }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    this._revoke(this._cache.get(cacheKey));
    this._cache.set(cacheKey, { url: objectUrl });
    return objectUrl;
  }
}

// Singleton — import this directly everywhere.
// Do not call new AssetResolver() elsewhere.
const assetResolver = new AssetResolver();
export default assetResolver;
